import type { Auction } from '@b-stock/auction-api-client'
import type {
  ContractDtoBidBasisEnum,
  ContractDtoBidUnitEnum,
} from '@b-stock/contract-api-client'
import { getOfferingOrInvoiceContractDefaultBidPrecision } from '@b-stock/fe-core/bstock-next/components'
import { usePrecisionNumberFormat } from '@b-stock/fe-core/bstock-react'
import type { ListingDtoPricingStrategyEnum } from '@b-stock/listing-api-client'
import type {
  OfferingDto,
  OfferingDtoOfferAmountUnitsEnum,
} from '@b-stock/offering-api-client'

export const useFormatContractBidAmount = ({
  bidBasis = 'ABSOLUTE',
  bidPrecision = 1,
  bidUnit = 'ITEMS',
  currency,
  pricingStrategy,
}: {
  bidBasis?: ContractDtoBidBasisEnum
  bidPrecision: Auction['bidPrecision'] | OfferingDto['bidPrecision']
  bidUnit?: ContractDtoBidUnitEnum
  currency?: string
  pricingStrategy: ListingDtoPricingStrategyEnum
}) => {
  const isAbsolute = bidBasis === 'ABSOLUTE'
  const isAuction = pricingStrategy === 'AUCTION'

  const { formatPrecisionNumber: formatContractBidAmount } =
    usePrecisionNumberFormat({
      precision:
        // because `fixedTerms` does not have `bidPrecision` yet, we are using default settings
        // C3-866 will clean this up once bidPrecision is available (via `order-process`)
        pricingStrategy === 'FIXED_TERMS'
          ? getOfferingOrInvoiceContractDefaultBidPrecision({
              bidBasis,
              bidUnit,
            })
          : /**
             * Special case: absolute auction.bidPrecision is in dollars
             * - convert to cents to match contract.bidAmount
             */
            isAuction && isAbsolute
            ? bidPrecision * 100
            : bidPrecision,
      format: isAbsolute ? 'currency' : 'percent',
      currency,
    })

  return formatContractBidAmount
}

export const useFormatSpotBidAmount = ({
  bidPrecision = 1,
  currency,
  offerAmountUnits,
  pricingStrategy,
}: {
  bidPrecision: Auction['bidPrecision'] | OfferingDto['bidPrecision']
  currency?: string
  offerAmountUnits?: OfferingDtoOfferAmountUnitsEnum
  pricingStrategy: ListingDtoPricingStrategyEnum
}) => {
  const isAuction = pricingStrategy === 'AUCTION'
  const isAbsolute = isAuction || offerAmountUnits === 'PRICE'

  const { formatPrecisionNumber: formatSpotBidAmount } =
    usePrecisionNumberFormat({
      precision: bidPrecision,
      precisionStored: isAuction ? 1 : undefined,
      format: isAbsolute ? 'currency' : 'percent',
      currency,
    })

  return formatSpotBidAmount
}
